import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Swords, Users, Play, Loader2, Copy, Check, X, Trophy,
  Star, Zap, Clock, ChevronRight, Shield, Target, Puzzle, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GAME_TYPES = [
  { key: 'math_battle', label: 'Math Battle', desc: 'Race to answer math questions!', icon: Swords, color: 'from-blue-500 to-indigo-600' },
  { key: 'reading_battle', label: 'Reading Battle', desc: 'Race through letters, sounds & words!', icon: Target, color: 'from-emerald-500 to-teal-600' },
  { key: 'science_quiz', label: 'Science Quiz', desc: 'Race through science facts!', icon: Zap, color: 'from-purple-500 to-fuchsia-600' },
  { key: 'bible_quiz', label: 'Bible Quiz Battle', desc: 'Race to answer Bible questions!', icon: Shield, color: 'from-red-500 to-orange-500' },
  { key: 'coop_puzzle', label: 'Word Puzzle Co-op', desc: 'Solve Bible words together!', icon: Puzzle, color: 'from-cyan-500 to-sky-600' },
];

// ==================== QUIZ BATTLE GAME ====================
const QuizBattleGame = ({ room, childId, token, onFinish }) => {
  const [currentQ, setCurrentQ] = useState(0);
  const [myScore, setMyScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  const [answered, setAnswered] = useState({});
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [results, setResults] = useState(null);
  const wsRef = useRef(null);

  const isHost = room.host?.id === childId;
  const questions = room.questions || [];
  const myName = isHost ? room.host?.name : room.guest?.name;
  const opponentName = isHost ? room.guest?.name : room.host?.name;

  useEffect(() => {
    // Connect WebSocket
    const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/multiplayer/ws/${room.code}/${childId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === 'answer_submitted') {
        if (isHost) {
          setMyScore(msg.host_score);
          setOpponentScore(msg.guest_score);
        } else {
          setMyScore(msg.guest_score);
          setOpponentScore(msg.host_score);
        }
        if (msg.game_status === 'finished') setGameOver(true);
      }
      if (msg.type === 'game_finished') {
        setGameOver(true);
        const won = (msg.winner === 'host' && isHost) || (msg.winner === 'guest' && !isHost);
        setResults({
          won: msg.winner === null ? null : won,
          myXP: isHost ? msg.host_xp : msg.guest_xp,
          opponentXP: isHost ? msg.guest_xp : msg.host_xp,
        });
      }
    };

    // Ping to keep alive
    const interval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
    }, 15000);

    return () => { clearInterval(interval); ws.close(); };
  }, [room.code, childId, isHost]);

  const submitAnswer = async (answer) => {
    if (answered[questions[currentQ]?.id]) return;
    setSelectedAnswer(answer);

    try {
      const r = await fetch(`${API_URL}/api/multiplayer/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          child_id: childId,
          room_code: room.code,
          question_id: questions[currentQ].id,
          answer,
        }),
      });
      const data = await r.json();
      setFeedback({ correct: data.correct, correctAnswer: data.correct_answer });
      setAnswered(p => ({ ...p, [questions[currentQ].id]: true }));
      setMyScore(data.your_score);

      // Auto-advance after delay
      setTimeout(() => {
        setFeedback(null);
        setSelectedAnswer(null);
        if (currentQ < questions.length - 1) {
          setCurrentQ(p => p + 1);
        } else if (data.game_status === 'finished') {
          setGameOver(true);
        }
      }, 1500);
    } catch (e) {
      toast.error('Failed to submit answer');
    }
  };

  if (gameOver || results) {
    return (
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-8">
        <Trophy className={`w-16 h-16 mx-auto mb-4 ${results?.won ? 'text-amber-400' : results?.won === false ? 'text-slate-400' : 'text-emerald-400'}`} />
        <h2 className="text-2xl font-black text-white mb-1">
          {results?.won === null ? "It's a Tie!" : results?.won ? 'You Won!' : 'Nice Try!'}
        </h2>
        <div className="flex justify-center gap-8 my-4">
          <div className="text-center">
            <p className="text-3xl font-black text-indigo-400">{myScore}</p>
            <p className="text-xs text-slate-400">{myName} (You)</p>
          </div>
          <div className="text-slate-600 text-2xl font-bold self-center">vs</div>
          <div className="text-center">
            <p className="text-3xl font-black text-red-400">{opponentScore}</p>
            <p className="text-xs text-slate-400">{opponentName}</p>
          </div>
        </div>
        {results && <p className="text-emerald-400 font-bold">+{results.myXP} XP earned!</p>}
        <Button onClick={onFinish} className="mt-6 bg-gradient-to-r from-indigo-500 to-purple-600" data-testid="back-to-lobby">
          <RotateCcw className="w-4 h-4 mr-2" /> Play Again
        </Button>
      </motion.div>
    );
  }

  const q = questions[currentQ];
  if (!q) return null;

  return (
    <div className="space-y-4">
      {/* Score bar */}
      <div className="flex items-center justify-between bg-slate-800/60 rounded-xl p-3">
        <div className="text-center">
          <p className="text-lg font-black text-indigo-400">{myScore}</p>
          <p className="text-[10px] text-slate-500">You</p>
        </div>
        <div className="flex-1 mx-4">
          <div className="flex justify-between text-[10px] text-slate-500 mb-1">
            <span>Q{currentQ + 1}/{questions.length}</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all" style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }} />
          </div>
        </div>
        <div className="text-center">
          <p className="text-lg font-black text-red-400">{opponentScore}</p>
          <p className="text-[10px] text-slate-500">{opponentName || 'Opponent'}</p>
        </div>
      </div>

      {/* Question */}
      <motion.div key={currentQ} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}>
        <Card className="bg-slate-800/60 border-slate-700/50">
          <CardContent className="p-5">
            <p className="text-white font-bold text-lg mb-4">{q.question}</p>
            <div className="grid grid-cols-2 gap-3">
              {q.options?.map((opt, i) => {
                const isSelected = selectedAnswer === opt;
                const isCorrect = feedback && opt === (feedback.correctAnswer || selectedAnswer);
                const isWrong = feedback && isSelected && !feedback.correct;
                return (
                  <motion.button key={i} whileTap={{ scale: 0.95 }}
                    onClick={() => !feedback && submitAnswer(opt)}
                    disabled={!!feedback}
                    data-testid={`quiz-option-${i}`}
                    className={`p-3 rounded-xl text-sm font-bold transition-all border-2 ${
                      isCorrect ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300' :
                      isWrong ? 'bg-red-500/20 border-red-500 text-red-300' :
                      isSelected ? 'bg-indigo-500/20 border-indigo-500 text-indigo-300' :
                      'bg-slate-700/50 border-slate-600 text-white hover:border-indigo-400'
                    }`}>
                    {opt}
                  </motion.button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

// ==================== COOP PUZZLE GAME ====================
const CoopPuzzleGame = ({ room, childId, token, onFinish }) => {
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [allSolved, setAllSolved] = useState(false);
  const [myScore, setMyScore] = useState(0);
  const questions = room.questions || [];

  const submitWord = async (q) => {
    const userAnswer = answers[q.id] || '';
    try {
      const r = await fetch(`${API_URL}/api/multiplayer/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          child_id: childId, room_code: room.code,
          question_id: q.id, answer: userAnswer,
        }),
      });
      const data = await r.json();
      setFeedback(p => ({ ...p, [q.id]: data.correct }));
      if (data.correct) {
        setMyScore(data.your_score);
        toast.success('Correct!');
      }
      if (data.game_status === 'finished') setAllSolved(true);
    } catch (e) { toast.error('Failed to submit'); }
  };

  if (allSolved) {
    return (
      <div className="text-center py-8">
        <Trophy className="w-16 h-16 mx-auto mb-4 text-amber-400" />
        <h2 className="text-2xl font-black text-white">Puzzle Complete!</h2>
        <p className="text-emerald-400 text-lg font-bold">{myScore} points earned</p>
        <Button onClick={onFinish} className="mt-6 bg-gradient-to-r from-blue-500 to-cyan-500" data-testid="coop-back-lobby">
          <RotateCcw className="w-4 h-4 mr-2" /> Play Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-center text-slate-400 text-sm mb-2">Solve the Bible words from clues!</p>
      {questions.map((q, i) => (
        <Card key={q.id} className={`border transition-all ${
          feedback[q.id] === true ? 'bg-emerald-500/10 border-emerald-500/40' :
          feedback[q.id] === false ? 'bg-red-500/10 border-red-500/30' :
          'bg-slate-800/40 border-slate-700/50'
        }`}>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm text-slate-300 mb-1">{q.clue}</p>
              <div className="flex gap-2">
                <Input placeholder={`${q.word?.length || 5} letters`}
                  value={answers[q.id] || ''}
                  onChange={e => setAnswers(p => ({ ...p, [q.id]: e.target.value }))}
                  disabled={feedback[q.id] === true}
                  className="bg-slate-700/50 border-slate-600 text-white uppercase font-mono tracking-widest"
                  data-testid={`coop-input-${i}`} />
                <Button size="sm" onClick={() => submitWord(q)} disabled={feedback[q.id] === true}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500" data-testid={`coop-submit-${i}`}>
                  <Check className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {feedback[q.id] === true && <Check className="w-6 h-6 text-emerald-400 flex-shrink-0" />}
            {feedback[q.id] === false && <X className="w-6 h-6 text-red-400 flex-shrink-0" />}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ==================== MAIN PAGE ====================
const MultiplayerGames = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState('menu'); // menu, create, join, waiting, playing
  const [selectedType, setSelectedType] = useState(null);
  const [roomCode, setRoomCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [room, setRoom] = useState(null);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [history, setHistory] = useState(null);
  const pollRef = useRef(null);
  const waitingWsRef = useRef(null);

  const getToken = () => localStorage.getItem('littlelearners-token') || localStorage.getItem('token');

  const fetchRoomState = async (code) => {
    const token = getToken();
    const rr = await fetch(`${API_URL}/api/multiplayer/room/${code}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!rr.ok) return null;
    return rr.json();
  };

  const teardownWaitingConnections = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (waitingWsRef.current) {
      try { waitingWsRef.current.close(); } catch (e) { void e; }
      waitingWsRef.current = null;
    }
  };

  useEffect(() => {
    const fetchChild = async () => {
      try {
        const token = getToken();
        const r = await fetch(`${API_URL}/api/christian/children`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        const children = data.children || data;
        const found = children.find(c => c.id === childId);
        setChild(found || { name: 'Player' });
      } catch (e) {
        // Try regular children
        try {
          const token = getToken();
          const r = await fetch(`${API_URL}/api/children/${childId}`, { headers: { Authorization: `Bearer ${token}` } });
          setChild(await r.json());
        } catch { setChild({ name: 'Player' }); }
      } finally { setLoading(false); }
    };
    fetchChild();
    return () => { teardownWaitingConnections(); };
  }, [childId]);

  const createRoom = async (gameType) => {
    setCreating(true);
    try {
      const token = getToken();
      const r = await fetch(`${API_URL}/api/multiplayer/create-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ child_id: childId, game_type: gameType, mode: 'realtime' }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not create room');
        return;
      }
      const data = await r.json();
      setRoomCode(data.room_code);
      setSelectedType(gameType);
      setScreen('waiting');

      // Handoff: switch host into playing screen once guest arrives.
      const handoff = async () => {
        const roomData = await fetchRoomState(data.room_code);
        if (roomData && roomData.status === 'playing') {
          teardownWaitingConnections();
          setRoom(roomData);
          setScreen('playing');
        }
      };

      // Real-time via WebSocket (broadcasts `player_joined`)
      try {
        const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/multiplayer/ws/${data.room_code}/${childId}`;
        const ws = new WebSocket(wsUrl);
        waitingWsRef.current = ws;
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === 'player_joined' || msg.status === 'playing') {
              handoff();
            }
          } catch (err) { void err; }
        };
        // Keep-alive pings so proxies don't drop the socket
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, 15000);
        ws.onclose = () => clearInterval(pingInterval);
      } catch (err) { void err; }

      // Redundant safety net: poll every 3s in case the WebSocket didn't deliver
      pollRef.current = setInterval(handoff, 3000);
    } catch (err) {
      toast.error('Failed to create room');
    } finally { setCreating(false); }
  };

  const joinRoom = async () => {
    if (!joinCode.trim()) return toast.error('Enter a room code');
    setJoining(true);
    try {
      const token = getToken();
      const r = await fetch(`${API_URL}/api/multiplayer/join-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ child_id: childId, room_code: joinCode.toUpperCase() }),
      });
      if (!r.ok) {
        const err = await r.json();
        toast.error(err.detail || 'Failed to join');
        return;
      }
      const data = await r.json();
      // Get full room state
      const rr = await fetch(`${API_URL}/api/multiplayer/room/${joinCode.toUpperCase()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const roomData = await rr.json();
      setRoom(roomData);
      setSelectedType(data.game_type);
      setScreen('playing');
    } catch (e) {
      toast.error('Failed to join room');
    } finally { setJoining(false); }
  };

  const fetchHistory = async () => {
    try {
      const token = getToken();
      const r = await fetch(`${API_URL}/api/multiplayer/history/${childId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(await r.json());
    } catch (err) { void err; }
  };

  const handleFinish = () => {
    setRoom(null);
    setRoomCode('');
    setSelectedType(null);
    setScreen('menu');
    teardownWaitingConnections();
  };

  const copyCode = () => {
    navigator.clipboard.writeText(roomCode);
    toast.success('Code copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-indigo-500/20 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="rounded-full text-white"
            onClick={() => screen === 'menu' ? navigate(-1) : handleFinish()} data-testid="mp-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-white flex items-center gap-2">
              <Swords className="w-5 h-5 text-red-400" /> Multiplayer
            </h1>
            <p className="text-xs text-indigo-300">{child?.name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* MENU */}
          {screen === 'menu' && (
            <motion.div key="menu" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white">Choose a Game</h2>
                <p className="text-slate-400 text-sm">Play with a friend!</p>
              </div>

              {/* Game type cards */}
              <div className="grid grid-cols-1 gap-4 mb-6">
                {GAME_TYPES.map(gt => (
                  <Card key={gt.key} className="bg-slate-800/40 border-slate-700/50 hover:border-indigo-500/50 cursor-pointer transition-all"
                    onClick={() => { setSelectedType(gt.key); setScreen('create'); }}
                    data-testid={`game-type-${gt.key}`}>
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gt.color} flex items-center justify-center shadow-md`}>
                        <gt.icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold">{gt.label}</h3>
                        <p className="text-slate-400 text-sm">{gt.desc}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600" />
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Join room */}
              <Card className="bg-slate-800/40 border-slate-700/50 mb-4">
                <CardContent className="p-4">
                  <p className="text-white font-bold text-sm mb-2">Join a Friend&apos;s Game</p>
                  <div className="flex gap-2">
                    <Input placeholder="Enter room code" value={joinCode}
                      onChange={e => setJoinCode(e.target.value.toUpperCase())} maxLength={6}
                      className="bg-slate-700/50 border-slate-600 text-white uppercase font-mono tracking-widest text-center text-lg"
                      data-testid="join-code-input" />
                    <Button onClick={joinRoom} disabled={joining}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500" data-testid="join-room-btn">
                      {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Join'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* History */}
              <Button onClick={fetchHistory} variant="ghost" className="w-full text-slate-500" data-testid="mp-history-btn">
                <Trophy className="w-4 h-4 mr-2" /> Game History
              </Button>
              {history && (
                <Card className="mt-3 bg-slate-800/40 border-slate-700/50">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-indigo-400">{history.summary.total_games}</p>
                        <p className="text-xs text-slate-500">Games</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-emerald-400">{history.summary.wins}</p>
                        <p className="text-xs text-slate-500">Wins</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-400">{history.summary.win_rate}%</p>
                        <p className="text-xs text-slate-500">Win Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {/* CREATE ROOM */}
          {screen === 'create' && (
            <motion.div key="create" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white">
                  {GAME_TYPES.find(g => g.key === selectedType)?.label}
                </h2>
                <p className="text-slate-400 text-sm">Create a room and share the code</p>
              </div>
              <Button onClick={() => createRoom(selectedType)} disabled={creating}
                className="w-full h-14 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-lg font-bold mb-4"
                data-testid="create-room-btn">
                {creating ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Swords className="w-5 h-5 mr-2" />}
                Create Room
              </Button>
              <Button onClick={() => setScreen('menu')} variant="ghost" className="w-full text-slate-400">
                Back to Menu
              </Button>
            </motion.div>
          )}

          {/* WAITING FOR PLAYER */}
          {screen === 'waiting' && (
            <motion.div key="waiting" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="text-center py-8">
              <Loader2 className="w-12 h-12 animate-spin text-indigo-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Waiting for opponent...</h2>
              <p className="text-slate-400 text-sm mb-6">Share this code with a friend:</p>
              <div className="inline-flex items-center gap-3 bg-slate-800/60 border border-slate-700 rounded-2xl px-6 py-4 mb-4">
                <span className="text-4xl font-black tracking-[0.3em] text-indigo-400 font-mono" data-testid="room-code-display">
                  {roomCode}
                </span>
                <Button variant="ghost" size="icon" onClick={copyCode} data-testid="copy-code-btn">
                  <Copy className="w-5 h-5 text-slate-400" />
                </Button>
              </div>
              <p className="text-xs text-slate-500">The game starts when a friend joins</p>
              <Button onClick={handleFinish} variant="ghost" className="mt-6 text-slate-500" data-testid="cancel-waiting">
                Cancel
              </Button>
            </motion.div>
          )}

          {/* PLAYING */}
          {screen === 'playing' && room && (
            <motion.div key="playing" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {selectedType !== 'coop_puzzle' && (
                <QuizBattleGame room={room} childId={childId} token={getToken()} onFinish={handleFinish} />
              )}
              {selectedType === 'coop_puzzle' && (
                <CoopPuzzleGame room={room} childId={childId} token={getToken()} onFinish={handleFinish} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default MultiplayerGames;
